import { CuriosityNamespace, CuriosityNamespaceType } from '@procureai/curiosity-ui-mui';

/**
 * Presentation config for savings change-log fields. houston emits stable
 * camelCase keys with raw values; this map owns every display decision:
 * - `labelKey`: sub-key under `common:Savings.form.fields` (reuses the form labels)
 * - `ns`: curiosity namespace to resolve a UUID value to a human label
 * - `format`: numeric/date preset applied to a raw value
 * - `enumPrefix`: `common` keyPrefix to translate an enum token
 * Keys not listed here still render (humanized label + raw value).
 */
export type SavingFieldFormat = 'date' | 'decimal';

export interface SavingFieldSpec {
  labelKey?: string;
  ns?: CuriosityNamespace;
  format?: SavingFieldFormat;
  enumPrefix?: string;
}

export const SAVING_FIELD_CONFIG: Record<string, SavingFieldSpec> = {
  // Scalar columns
  name: { labelKey: 'name' },
  status: { labelKey: 'status', enumPrefix: 'SavingStatus' },
  description: { labelKey: 'description' },
  freeTextCommodity: { labelKey: 'commodity' },
  currency: { labelKey: 'currency' },
  reductionType: { labelKey: 'reductionType', enumPrefix: 'SavingReductionType' },
  baselinePrice: { labelKey: 'baselinePrice', format: 'decimal' },
  newPrice: { labelKey: 'newPrice', format: 'decimal' },
  baselineQuantity: { labelKey: 'baselineQuantity', format: 'decimal' },
  newQuantity: { labelKey: 'newQuantity', format: 'decimal' },
  originalPriceQuantity: { labelKey: 'originalPriceQuantity', format: 'decimal' },
  newPriceQuantity: { labelKey: 'newPriceQuantity', format: 'decimal' },
  validFrom: { labelKey: 'validFrom', format: 'date' },
  validTo: { labelKey: 'validTo', format: 'date' },
  fkSupplier: { labelKey: 'supplier', ns: CuriosityNamespaceType.SUPPLIER },
  fkOrganization: { labelKey: 'organization', ns: CuriosityNamespaceType.ORGANIZATION },
  fkProcurementOrganization: {
    labelKey: 'procurementOrganization',
    ns: CuriosityNamespaceType.PROCUREMENT_ORGANIZATION,
  },
  fkCommodityGroup: { labelKey: 'commodityGroup', ns: CuriosityNamespaceType.COMMODITY_GROUP },
  fkPurchasingGroup: { labelKey: 'purchasingGroup', ns: CuriosityNamespaceType.PURCHASING_GROUP },
  fkUnit: { labelKey: 'unit', ns: CuriosityNamespaceType.UNIT },
  fkAccountingArea: { labelKey: 'accountingArea', ns: CuriosityNamespaceType.ACCOUNTING_AREA },
  fkBuyer: { ns: CuriosityNamespaceType.INTERNAL_USER },
  // cost_allocation items
  fkGeneralLedgerAccount: { labelKey: 'glAccount', ns: CuriosityNamespaceType.GL_ACCOUNT },
  allocationValue: { labelKey: 'allocationValue', format: 'decimal' },
  distributionType: {
    labelKey: 'distributionType',
    enumPrefix: 'SavingCostAllocationDistributionType',
  },
  fkAccountAssignment: {
    labelKey: 'accountAssignmentValue',
    ns: CuriosityNamespaceType.ACCOUNT_ASSIGNMENT,
  },
  fkAccountAssignmentCategory: {
    labelKey: 'accountAssignmentCategory',
    ns: CuriosityNamespaceType.ACCOUNT_ASSIGNMENT_CATEGORY,
  },
  // source_references items
  sourceReference: { labelKey: 'purchaseRequisition', ns: CuriosityNamespaceType.PURCHASE_REQUISITION },
  sourceReferenceItem: { labelKey: 'purchaseRequisitionItem', ns: CuriosityNamespaceType.PURCHASE_REQUISITION_ITEM },
  fkSourceSystem: { ns: CuriosityNamespaceType.SOURCE_SYSTEM },
  synchronizedAt: { format: 'date' },
};

/** Fallback label for keys without a config entry: "fkBuyer" -> "Buyer". */
export const humanizeFieldKey = (key: string): string => {
  const spaced = key
    .replace(/^fk/, '')
    .replace(/([A-Z])/g, ' $1')
    .trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};
